import { useState } from 'react';
import type { ProcessedReport } from '@integratio/core';
import { ExternalLink, RotateCcw } from 'lucide-react';
import { formatDateTime, formatPercent } from '../lib/format';
import { ChartCard } from './charts/ChartCard';
import { ContextEnvironmentChart } from './charts/ContextEnvironmentChart';
import { PriorityChart } from './charts/PriorityChart';
import { BehaviorChart } from './charts/BehaviorChart';
import { PowerInterestMatrix } from './charts/PowerInterestMatrix';
import { MatrixChart } from './charts/MatrixChart';
import { PdfExportButton } from './PdfExportButton';
import { PngExportButton } from './PngExportButton';
import { PptExportButton } from './PptExportButton';

type TabKey = 'contexto' | 'prioridade' | 'postura' | 'poder-interesses' | 'tabela';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'contexto', label: 'Contexto do Ambiente' },
  { key: 'prioridade', label: 'Prioridade de Atuação' },
  { key: 'postura', label: 'Postura e Comportamento' },
  { key: 'poder-interesses', label: 'Poder x Interesses' },
  { key: 'tabela', label: 'Tabela resumida' },
];

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }): JSX.Element {
  return (
    <div className="card p-5">
      <p className="eyebrow">{label}</p>
      <p className="stat-value mt-2">{value}</p>
      {hint && <p className="mt-1 text-xs text-brand-slate">{hint}</p>}
    </div>
  );
}

export function ReportDashboard({
  report,
  onRestart,
}: {
  report: ProcessedReport;
  onRestart: () => void;
}): JSX.Element {
  const [tab, setTab] = useState<TabKey>('contexto');

  const topContext = [...report.contextGroups].sort((a, b) => b.count - a.count)[0];
  const highPriority = report.priorityGroups.find((group) => group.level === 'ALTA');
  const topBehavior = [...report.behaviorGroups].sort((a, b) => b.count - a.count)[0];

  return (
    <div className="space-y-6">
      <section className="card flex flex-wrap items-center justify-between gap-4 p-6">
        <div>
          <p className="eyebrow">Relatório gerado</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-brand-ink">{report.fileName}</h1>
          <p className="mt-1 text-sm text-brand-slate">
            {formatDateTime(report.generatedAt)} · {report.totalStakeholders} stakeholders processados
            {report.warningsCount > 0 ? ` · ${report.warningsCount} alerta(s) na conferência` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className="btn-ghost" onClick={onRestart}>
            <RotateCcw className="h-4 w-4" aria-hidden />
            Nova planilha
          </button>
          <a
            className="btn-secondary"
            href={`/print/${report.reportId}`}
            target="_blank"
            rel="noreferrer"
          >
            <ExternalLink className="h-4 w-4" aria-hidden />
            Pré-visualizar relatório
          </a>
          <PdfExportButton reportId={report.reportId} label="Baixar PDF" variant="primary" />
          <PptExportButton reportId={report.reportId} label="Baixar PPT" />
          <PngExportButton reportId={report.reportId} label="Baixar PNG (ZIP)" />
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi label="Total de stakeholders" value={String(report.totalStakeholders)} hint={`${report.totalRowsImported} linhas lidas`} />
        <Kpi
          label="Contexto predominante"
          value={topContext ? topContext.name : '—'}
          hint={topContext ? `${formatPercent(topContext.percentage)} · ${topContext.count} stakeholders` : undefined}
        />
        <Kpi
          label="Prioridade alta"
          value={highPriority ? formatPercent(highPriority.percentage) : '—'}
          hint={highPriority ? `${highPriority.count} stakeholders` : undefined}
        />
        <Kpi
          label="Postura predominante"
          value={topBehavior ? topBehavior.name : '—'}
          hint={topBehavior ? `${formatPercent(topBehavior.percentage)} · ${topBehavior.count} stakeholders` : undefined}
        />
      </section>

      <nav className="no-print flex flex-wrap gap-2" aria-label="Gráficos do relatório">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={[
              'rounded-xl border px-4 py-2 text-sm font-semibold transition',
              tab === item.key
                ? 'border-brand-red bg-brand-red text-white shadow-card'
                : 'border-brand-line bg-white text-brand-slate hover:border-brand-slate hover:text-brand-ink',
            ].join(' ')}
            aria-pressed={tab === item.key}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {tab === 'contexto' && (
        <ChartCard
          eyebrow="Análise de Stakeholders"
          title="Contexto do Ambiente"
          subtitle="Classificação por posicionamento e poder. A altura de cada bloco é proporcional ao percentual do grupo."
          actions={
            <>
              <PdfExportButton reportId={report.reportId} chart="contexto" label="Exportar PDF" />
              <PngExportButton reportId={report.reportId} label="Exportar PNG (ZIP)" />
            </>
          }
          footnote="Percentuais calculados sobre o total de stakeholders importados."
        >
          <ContextEnvironmentChart groups={report.contextGroups} total={report.totalStakeholders} />
          <div className="mt-8 border-t border-brand-line pt-6">
            <p className="eyebrow mb-4">Detalhamento por quadrante</p>
            <MatrixChart
              groups={report.contextGroups}
              rows={3}
              cols={2}
              total={report.totalStakeholders}
              axisX={{ label: 'Poder' }}
              axisY={{ label: 'Posicionamento' }}
            />
          </div>
        </ChartCard>
      )}

      {tab === 'prioridade' && (
        <ChartCard
          eyebrow="Análise de Stakeholders"
          title="Prioridade de Atuação"
          subtitle="Distribuição por nível de prioridade e ação recomendada, conforme a coluna Prioridade da planilha."
          actions={
            <>
              <PdfExportButton reportId={report.reportId} chart="prioridade" label="Exportar PDF" />
              <PngExportButton reportId={report.reportId} label="Exportar PNG (ZIP)" />
            </>
          }
          footnote="Os subgrupos preservam a numeração e o texto original da planilha."
        >
          <PriorityChart groups={report.priorityGroups} total={report.totalStakeholders} />
        </ChartCard>
      )}

      {tab === 'postura' && (
        <ChartCard
          eyebrow="Análise de Stakeholders"
          title="Postura e Comportamento"
          subtitle="Matriz de influência e interesses (coluna Poder X Interesses). O número sobre cada bloco é a quantidade de stakeholders."
          actions={
            <>
              <PdfExportButton reportId={report.reportId} chart="postura" label="Exportar PDF" />
              <PngExportButton reportId={report.reportId} label="Exportar PNG (ZIP)" />
            </>
          }
        >
          <BehaviorChart groups={report.behaviorGroups} total={report.totalStakeholders} />
          <div className="mt-8 border-t border-brand-line pt-6">
            <p className="eyebrow mb-4">Detalhamento por quadrante</p>
            <MatrixChart
              groups={report.behaviorGroups}
              rows={2}
              cols={2}
              total={report.totalStakeholders}
              axisX={{ label: 'Interesses' }}
              axisY={{ label: 'Influência' }}
            />
          </div>
        </ChartCard>
      )}

      {tab === 'poder-interesses' && (
        <ChartCard
          eyebrow="Análise de Stakeholders"
          title="Poder x Interesses"
          subtitle="Dispersão dos valores de Poder e Interesses informados na planilha, com os quadrantes de postura."
          actions={
            <>
              <PdfExportButton reportId={report.reportId} chart="poder-interesses" label="Exportar PDF" />
              <PngExportButton reportId={report.reportId} label="Exportar PNG (ZIP)" />
            </>
          }
        >
          <PowerInterestMatrix data={report.powerInterest} total={report.totalStakeholders} />
        </ChartCard>
      )}

      {tab === 'tabela' && (
        <ChartCard
          eyebrow="Análise de Stakeholders"
          title="Tabela resumida"
          subtitle="Stakeholders processados, em ordem alfabética."
          actions={
            <>
              <PdfExportButton reportId={report.reportId} chart="tabela" label="Exportar PDF" />
              <PptExportButton reportId={report.reportId} label="Exportar PPT" />
              <PngExportButton reportId={report.reportId} label="Exportar PNG (ZIP)" />
            </>
          }
        >
          <div className="overflow-auto">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead className="bg-brand-mist text-left text-[11px] uppercase tracking-wider text-brand-slate">
                <tr>
                  <th className="px-4 py-3 font-semibold">Nº</th>
                  <th className="px-4 py-3 font-semibold">Stakeholder</th>
                  <th className="px-4 py-3 font-semibold">Empresa/Instituição</th>
                  <th className="px-4 py-3 font-semibold">Cargo</th>
                  <th className="px-4 py-3 font-semibold">Categoria</th>
                </tr>
              </thead>
              <tbody>
                {report.stakeholders.map((item) => (
                  <tr key={item.id} className="border-t border-brand-line/70">
                    <td className="px-4 py-2.5 tabular-nums text-brand-slate">{item.numero || '—'}</td>
                    <td className="px-4 py-2.5 font-medium text-brand-ink">{item.nome}</td>
                    <td className="px-4 py-2.5 text-brand-slate">{item.empresaInstituicao || '—'}</td>
                    <td className="px-4 py-2.5 text-brand-slate">{item.cargo || '—'}</td>
                    <td className="px-4 py-2.5 text-brand-slate">{item.categoria || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>
      )}
    </div>
  );
}
